import { Choice, Information, LockClosed } from '@sas/react-svg';
import {
  createElement,
  forwardRef,
  ReactNode,
  RefObject,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Item } from '..';
import { useParentSize, UseParentSizeProps } from '../../../hooks/useParentSize';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import { Icon } from '../../Icon';
import { Label } from '../../Label';
import { Flow } from '../../Layouts/Flow';
import { SearchField } from '../../SearchField';
import { TableColumnProps, TableRegions } from '../../Table';
import { useTreeTableState, CellNode, TreeTable, DEFAULT_INDENTATION } from '../../TreeTable';
import { Text } from '../../Text';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { useComparator } from '../../../locale/sorting';
import { DualSelectorSortProps } from '../types';

import './DualSelectorLayout.css';
import cn from './DualSelectorLayout.css.json';

/**
 * In the mode of 'keep', an available item can be added from source to target multiple times.
 * This property indicates how to display duplicated items.
 * - 'single' indicates the duplicated item will be displayed as one item with counter in following parenttheses.
 * - 'multiple' indicates the duplicated item can be listed multiple times in selected list and the order is as the order it is added and the order can be adjusted by order buttons.
 *
 * For other modes, 'move' and 'disable', the source and target layouts display items like multiple display style in 'keep' mode but there is no duplicated items in them.
 */
export type DisplayStyle = 'single' | 'multiple';
export interface DualSelectorLayoutProps extends TestIdProps {
  tableName: 'source' | 'target';
  type: string;
  mode: string;
  dataFieldKeys: Array<string>;
  data: Array<Item>;
  hideSearch?: boolean;
  alwaysSelected?: Array<Item>;
  customItems?: Array<Item>;
  selections?: Array<string>;
  disableFilter: boolean;
  selectionCallback: (
    selectedRowsArray: Array<string> | null | undefined,
    tableName: 'source' | 'target'
  ) => void;
  onDoubleClick: (selectedRow: number, tableName: 'source' | 'target') => void;
  filterValue: (value: string, tableName: 'source' | 'target') => void;
  searchAriaLabel: string;
  focusAfterAction: boolean;
  descriptionField?: string;
  displayStyle?: DisplayStyle;
  zeroStateText?: string;
  isDataReset?: boolean;
  sortFunction?: DualSelectorSortProps['sortFunction'];
  disableSort?: DualSelectorSortProps['disableSort'];
  numberOfResults?: number | null;
}

export const isAlwaysSelectedItem = (item: Item) => {
  return item._alwaysSelected ?? false;
};

type DisplayItem = {
  data: Item;
  key: string;
  count: number;
  originRowIndex: number;
};

const getChildNodes = (key: string, forData: DisplayItem[]): DisplayItem[] => {
  const index = parseInt(key);
  const data = forData[index];

  if (!data || !data.data.hasChildren) return [];
  const nodes: DisplayItem[] = [];

  let nextLevel = (data.data.level || 0) + 1;
  for (let rowIndex = index + 1; rowIndex < forData.length; rowIndex++) {
    const { level, hasChildren } = forData[rowIndex].data;
    if (level === nextLevel) nodes.push(forData[rowIndex]);
    if (hasChildren) nextLevel++;
  }

  return nodes;
};

const getChildNodesRowKey = (key: string, forData: DisplayItem[]): string[] => {
  return getChildNodes(key, forData).map((node) => node.key);
};

const getParentsRowKey = (key: string, forData: DisplayItem[]) => {
  const keys: string[] = [];
  const index = parseInt(key);

  if (index !== -1) {
    const data = forData[index];
    let parentLevel = data.data.level - 1;
    for (let rowIndex = index; rowIndex >= 0; rowIndex--) {
      const { level } = forData[rowIndex].data;
      if (level === parentLevel) {
        keys.push(forData[rowIndex].key);
        --parentLevel;
      }
    }
  }

  return keys;
};

const getDirectChildNodesRowKey = (key: string, forData: DisplayItem[]) => {
  const keys = [];
  const index = parseInt(key);
  const data = forData[index];
  if (!data || !data.data.hasChildren) return [];
  const nextLevel = data.data.level + 1;
  for (let rowIndex = index + 1; rowIndex < forData.length; rowIndex++) {
    const { level } = forData[rowIndex].data;
    if (level === nextLevel) keys.push(forData[rowIndex].key);
    if (level > nextLevel) continue;
    if (level === data.data.level) break;
  }
  return keys;
};

const getAllLevelsChildNodesRowKey = (key: string, forData: DisplayItem[]) => {
  let childRowKeys = getDirectChildNodesRowKey(key, forData);
  childRowKeys.forEach((childRowKey) => {
    childRowKeys = [...childRowKeys, ...getAllLevelsChildNodesRowKey(childRowKey, forData)];
  });
  return childRowKeys;
};

const normalizedData = (expNodes: ReadonlySet<string>, forData: DisplayItem[]) => {
  const newData: DisplayItem[] = [];
  let count = 0;
  for (let rowIndex = 0; rowIndex < forData.length; rowIndex++) {
    const rowData = forData[rowIndex];
    const { id, hasChildren } = rowData.data;
    const isExpanded = expNodes.has(id);
    newData[count] = { ...rowData };
    count++;
    if (!isExpanded && hasChildren) {
      const children = getAllLevelsChildNodesRowKey(rowData.key, forData);
      rowIndex += children.length;
    }
  }
  return newData;
};

export const DualSelectorLayout = forwardRef((props: DualSelectorLayoutProps, ref) => {
  let columns: TableColumnProps[] = [];
  const getData = props.data;
  const initialDimension: UseParentSizeProps = { initialDimensions: { height: 500, width: 500 } };
  const [setRef, dimensions] = useParentSize(initialDimension);
  const [searchValue, setSearchValue] = useState('');
  const [selectionState, setSelectionState] = useState<{
    selectAll: boolean | 'mixed' | undefined;
    selectedRows: Array<string> | null | undefined;
  }>({ selectAll: false, selectedRows: undefined });
  const localize = useNCTextLocalizer('DualSelector');
  const dt = useDesignTokens();
  const descriptionField = props.descriptionField;

  useImperativeHandle(ref, () => ({
    reset: () => {
      setSearchValue('');
    },
  }));

  const defaultComparator = useComparator<Item>({
    toSortKey: (t) => t[props.dataFieldKeys[0]],
    direction: 'ascending',
  });
  /* ---------------------------------------------------------
   * create Custom Cell when icon is present in the list
   * ---------------------------------------------------------*/
  const descriptionTokens = renderInlineCssVars(
    {
      'font-size-s': dt.fontNova.size.s,
    },
    'description'
  );
  /* ---------------------------------------------------------
   * create Custom Cell when icon is present in the list
   * ---------------------------------------------------------*/
  const tokens = renderInlineCssVars(
    {
      'spacing-s': dt.spacing.core.s,
    },
    'dualselector-list'
  );
  const customCell = (
    columnIndex: number,
    rowIndex: number,
    _columnKey?: string,
    _ref?: RefObject<any>,
    disabled?: boolean
  ) => {
    const item = displayedData[rowIndex].data;
    const textIconTestId = `listWithIcon${rowIndex}`;
    const alwaysSelected = (props.tableName === 'target' && item._alwaysSelected) ?? false;
    const bCustomItem = props.tableName === 'target' && item._custom;
    const keyFieldValue = item[props.dataFieldKeys[columnIndex]];
    const count = displayedData[rowIndex].count;
    const targetDisplayText = keyFieldValue.toString() + (count > 1 ? ` (${count})` : '');
    const sourceDisplayText = keyFieldValue.toString();
    return (
      <div className={cn.label} style={tokens}>
        {item?.icon ? (
          <Icon
            className={cn.listIcon}
            {...('icon' in item.icon ? { ...item.icon } : { icon: item.icon })}
            testId={postfixTestId(props.testId, textIconTestId)}
            disabled={disabled}
          />
        ) : null}

        <Flow orientation={'vertical'} spacing="none" className={cn.text}>
          <Text wrapText={false} disabled={disabled}>
            {props.tableName === 'target' ? targetDisplayText : sourceDisplayText}
          </Text>
          {descriptionField && item[descriptionField] ? (
            <Label
              wrapText={false}
              style={descriptionTokens}
              className={cn.descriptionText}
              testId={postfixTestId(props.testId, 'descriptionLabel')}
              disabled={disabled}
            >
              {item[descriptionField]}
            </Label>
          ) : null}
        </Flow>
        {alwaysSelected ? (
          <Icon
            className={cn.icon}
            icon={LockClosed}
            tooltip={localize('lockClosed.tooltip.label')}
          />
        ) : null}
        {bCustomItem ? (
          <Icon
            className={cn.icon}
            icon={Choice}
            tooltip={localize('customItem.choiceIcon.tooltip.label')}
            testId={postfixTestId(props.testId, 'choiceIcon')}
          />
        ) : null}
      </div>
    );
  };

  const customTreeCell = (rowIndex: number, children: ReactNode) => {
    const item = displayedData[rowIndex].data;
    const alwaysSelected =
      (props.tableName === 'target' && displayedData[rowIndex].data._alwaysSelected) ?? false;
    const bCustomItem = props.tableName === 'target' && displayedData[rowIndex].data._custom;
    return (
      <div className={cn.label} style={tokens}>
        <Flow orientation={'vertical'} spacing="none" className={cn.text}>
          {children}
          {descriptionField && item[descriptionField] ? (
            <Label
              wrapText={false}
              style={{
                ...descriptionTokens,
                marginInlineStart: `${DEFAULT_INDENTATION * (item.level || 0)}px`,
              }}
              className={cn.descriptionText}
              testId={postfixTestId(props.testId, 'descriptionLabel')}
            >
              {item[descriptionField]}
            </Label>
          ) : null}
        </Flow>
        {alwaysSelected ? (
          <Icon
            className={cn.icon}
            icon={LockClosed}
            tooltip={localize('lockClosed.tooltip.label')}
          />
        ) : null}
        {bCustomItem ? (
          <Icon
            className={cn.icon}
            icon={Choice}
            tooltip={localize('customItem.choiceIcon.tooltip.txt')}
            testId={postfixTestId(props.testId, 'choiceIcon')}
          />
        ) : null}
      </div>
    );
  };

  /* -------------------------------------------
   * create column definitions - header
   * ---------------------------------------------*/
  const displaySourceHeaders = (
    dataKeys: Array<string>,
    textIconPresent: boolean,
    descriptionField?: string
  ) => {
    const columns: TableColumnProps[] = [];
    for (const [index, dataKey] of dataKeys.entries()) {
      columns.push({
        key: `_${index}`,
        label: dataKey,
        cellContent:
          props.type !== 'tree' && (textIconPresent || descriptionField) ? customCell : undefined,
        maximumWidth: 2000,
      });
    }
    return columns;
  };

  /* -------------------------------------------
   * Check for zero state
   * ---------------------------------------------*/
  if (props.tableName === 'source' && props.type !== 'tree' && !props.disableSort) {
    getData.sort(props.sortFunction ?? defaultComparator);
  }
  if (props.tableName === 'target' && props.type !== 'tree') {
    props.sortFunction && getData.sort(props.sortFunction);
  }
  const dataValues: Array<Item> = useMemo(() => {
    const length = getData.length;
    if (length > 0) {
      const originData = Object.keys(getData).map((key: any) => getData[key]);

      if (props.tableName === 'source' && props.type === 'tree') {
        // always make sure source data is sorted - for correct parent/child dependency
        originData.sort((a: any, b: any) => {
          const key = 'index';
          let comparison = 0;
          if (a[key] > b[key]) {
            comparison = 1;
          } else if (a[key] < b[key]) {
            comparison = -1;
          }
          return comparison;
        });
      }

      return originData;
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData.length, getData]);

  const displayData: DisplayItem[] = useMemo(() => {
    if (props.tableName === 'target' && props.displayStyle === 'single') {
      return generateSingleDisplayData(dataValues);
    } else {
      return generateMultipleDisplayData(dataValues, props.type);
    }
  }, [dataValues, props.type, props.displayStyle, props.tableName]);

  if (!(getData === null || (getData === undefined && !props.alwaysSelected))) {
    if (dataValues.length > 0) {
      // Get keys array for number of columns
      const dataKeys: Array<string> = [];
      // In case of list display only one column based on props.dataFieldKeys
      if (props.dataFieldKeys !== null && props.dataFieldKeys !== undefined) {
        dataKeys.push(props.dataFieldKeys[0]);
        const textIconPresent = dataValues.some((dv) => dv.icon && dv.icon.toString().length > 0);
        columns = displaySourceHeaders(dataKeys, textIconPresent, descriptionField);
        const infoTextPresent = dataValues.some((dv) => dv.infoText);
        infoTextPresent &&
          columns.push({
            key: 'info',
            label: '',
            width: 32,
          });
      }
    }
  }

  const selectionsInDisplayData = useMemo(
    () =>
      props.selections
        ? getSelectionsInDisplayData(props.selections, dataValues, displayData)
        : undefined,
    [dataValues, displayData, props.selections]
  );
  useEffect(() => {
    if (props.disableFilter) {
      setSearchValue('');
    }
  }, [props.disableFilter]);

  const { onExpand, ...tableState } = useTreeTableState(
    new Set(displayData.filter((item) => item.data.expanded).map((i) => i.data.id)),
    {
      selectedRows: new Set(selectionsInDisplayData),
      selectAll: selectionState.selectAll,
    }
  );

  const [displayedData, setDisplayedData] = useState<DisplayItem[]>(
    normalizedData(tableState.expandedNodes, displayData)
  );
  const [numberOfRows, setNumberOfRows] = useState(displayedData.length);

  const prevDisplayedData = usePreviousValue(displayedData);

  useEffect(() => {
    const newDisplayData = normalizedData(tableState.expandedNodes, displayData);
    setDisplayedData(newDisplayData);
    setNumberOfRows(newDisplayData.length);
  }, [tableState.expandedNodes, displayData, searchValue]);

  useEffect(() => {
    const setSelectRows = tableState.setSelectRows;
    const scrollTo = tableState.scrollTo;
    const setExpandedNodes = tableState.setExpandedNodes;
    const clearMeasurementCache = tableState.clearMeasurementCache;

    clearMeasurementCache({ startColumnIndex: 0, startRowIndex: 0 });

    // expand a selected branch if required
    const expNodes = new Set(tableState.expandedNodes);
    selectionsInDisplayData?.forEach((selectedKey) => {
      const parentKeys = getParentsRowKey(selectedKey, displayData);
      parentKeys.forEach((parentKey: string) => {
        const parentNode = displayData.find((value) => value.key === parentKey);
        const parentNodeId = parentNode?.data.id;
        if (!expNodes.has(parentNodeId)) {
          expNodes.add(parentNodeId);
        }
      });
    });
    if (expNodes.size !== tableState.expandedNodes.size) {
      setExpandedNodes(expNodes);
    }

    setSelectRows({
      selectedRows: new Set(selectionsInDisplayData),
      rowCount: dataValues.length,
    });

    let scrollIndex = -1;

    if (props.focusAfterAction) {
      if (Array.isArray(selectionsInDisplayData) && selectionsInDisplayData.length > 0) {
        scrollIndex = parseInt(selectionsInDisplayData[0]);
      } else if (props.isDataReset) {
        scrollIndex = 0;
      } else {
        // is the ability to track and scroll to the new item being added.
        // This is coded for target list in all modes and source list only in move mode.
        // Do we need this for source list in keep and disabled mode in future?
        if (Array.isArray(prevDisplayedData) && displayedData.length > prevDisplayedData.length) {
          scrollIndex = prevDisplayedData.findIndex(
            (value, index) =>
              value.data[props.dataFieldKeys[0]] !==
              displayedData[index].data[props.dataFieldKeys[0]]
          );
          if (scrollIndex === -1) {
            scrollIndex = prevDisplayedData.length + 1;
          }
        }
      }

      if (
        scrollIndex !== -1 &&
        scrollIndex !== tableState.preference.scrollPosition?.cell.rowIndex
      ) {
        scrollTo({
          cell: {
            rowIndex: scrollIndex,
            colIndex: 1,
            region: TableRegions.Data,
          },
          align: 'smart',
        });
      }
    }
  }, [
    prevDisplayedData,
    props.isDataReset,
    displayData,
    displayedData,
    props.dataFieldKeys,
    props.tableName,
    selectionsInDisplayData,
    props.focusAfterAction,
    dataValues.length,
    tableState.expandedNodes,
    tableState.setSelectRows,
    tableState.scrollTo,
    tableState.setSelectRow,
    tableState.setExpandedNodes,
    tableState.preference.scrollPosition,
    tableState.clearMeasurementCache,
  ]);

  const customItems = props.customItems;
  useEffect(() => {
    /* -------------------------------------------
     * Get selected rows
     * ---------------------------------------------*/
    const getSelectedRows = (
      selectedRows: Array<string> | null | undefined,
      selectAll: boolean | 'mixed' | undefined
    ) => {
      const maxSelectedIndex = Math.max(-1, ...(selectedRows ?? []).map((item) => parseInt(item)));
      const selections =
        maxSelectedIndex < displayData.length ? selectedRows : selectionsInDisplayData;
      const selectedOriginRows: string[] = (selections ?? []).map((row) => {
        return displayData[parseInt(row)].originRowIndex.toString();
      });
      const selectionCallback = props.selectionCallback;
      const tableName = props.tableName;
      selectionCallback(
        (!selectedRows && selectAll && selectAll !== 'mixed'
          ? [...dataValues.keys()].map(String)
          : selectedOriginRows
        ).filter((rowKey) => !dataValues[parseInt(rowKey)]?.hasChildren),
        tableName
      );
    };
    if (selectionState.selectedRows || selectionState.selectAll) {
      getSelectedRows(selectionState.selectedRows, selectionState.selectAll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionState]);

  const focusedCell = useRef({
    rowIndex: 0,
    region: TableRegions.Data,
    colIndex: 0,
  });

  const tableStyles = renderInlineCssVars({
    'table-scroll-bar-space': '-.25rem',
    'tree-hover-bg': 'transparent',
  });

  const defaultZeroStateText =
    props.tableName === 'target'
      ? localize('emptySelectedMessage.msg')
      : localize('emptyAvailableMessage.msg');
  const zeroStateText = props.zeroStateText ?? defaultZeroStateText;
  const key = props.dataFieldKeys[0];
  return (
    <div className={cn.tableContent} data-testid={props.testId}>
      {!props.hideSearch ? (
        <div className={cn.listSearchField} style={tokens}>
          <SearchField
            testId={postfixTestId(props.testId, 'search')}
            className={cn.searchField}
            style={{
              minWidth: '100%',
            }}
            value={searchValue}
            placeholder={localize('filter.placeholder.label')}
            onChange={(e) => {
              setSearchValue(e.value);
              props.filterValue(e.value, props.tableName);
            }}
            disabled={props.disableFilter}
            ariaLabel={props.searchAriaLabel}
            filterMode
            numberOfResults={props.numberOfResults}
          />
        </div>
      ) : null}
      {dimensions ? (
        <div className={cn.tableList}>
          <div className={cn.tableListInner} ref={setRef}>
            <TreeTable
              role={props.type === 'tree' ? 'treegrid' : 'grid'}
              {...tableState}
              style={tableStyles}
              testId={postfixTestId(props.testId, `${props.tableName}Table`)}
              key={props.tableName}
              rowCount={numberOfRows}
              height={dimensions.height}
              width={dimensions.width}
              columns={columns}
              getRowKey={(rowIndex) => {
                if (rowIndex >= displayedData.length || rowIndex < 0) return '';
                return displayedData[rowIndex].key;
              }}
              getRowIndex={(rowKey) => {
                return parseInt(rowKey);
              }}
              getDataNode={(rowIndex) => {
                // return a data node for trees - this will trigger the getDataNodeContainer method
                // - so we can render our custom tree structure
                return props.type === 'tree' && displayedData[rowIndex]
                  ? (displayedData[rowIndex].data as CellNode)
                  : undefined;
              }}
              getDataNodeContainer={customTreeCell}
              getChildNodesRowKey={(rowKey) => {
                return getChildNodesRowKey(rowKey, displayData);
              }}
              getParentsRowKey={(key) => {
                const index = parseInt(key);
                const keys = [];
                const data = displayedData[index];
                let parentLevel = (data.data.level || 1) - 1;
                for (let rowIndex = index; rowIndex >= 0; rowIndex--) {
                  const { level } = displayedData[rowIndex].data;
                  if (level === parentLevel) {
                    keys.push(displayedData[rowIndex].key);
                    --parentLevel;
                  }
                }
                return keys;
              }}
              onExpand={(node, expanded) => {
                const nodes = new Set(tableState.expandedNodes);
                if (nodes.has(node.id) && !expanded) nodes.delete(node.id);
                else if (!nodes.has(node.id) && expanded) nodes.add(node.id);
                const data = normalizedData(nodes, displayData);
                setDisplayedData(data);
                onExpand(node, expanded);
                setNumberOfRows(data.length);
              }}
              renderCellData={({ rowIndex, columnKey, columnIndex, disabled }) => {
                const displayItem = displayedData[rowIndex];

                if (columnKey === 'info') {
                  // special info column
                  return displayItem.data.infoText ? (
                    <Icon
                      icon={Information}
                      tooltip={displayItem.data.infoText}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      disabled={disabled}
                    />
                  ) : null;
                }

                const keyFieldValue = displayItem.data[props.dataFieldKeys[columnIndex]];
                const count = displayItem.count;
                const targetDisplayText =
                  keyFieldValue.toString() + (count > 1 ? ` (${count})` : '');
                const sourceDisplayText = keyFieldValue.toString();
                const alwaysSelected =
                  props.tableName === 'target' && isAlwaysSelectedItem(displayItem.data);
                const bCustomItem = props.tableName === 'target' && displayItem.data._custom;

                return (
                  <Flow spacing="xs" className={cn.flowContainer}>
                    <Text className={cn.text} wrapText={false} disabled={disabled}>
                      {props.tableName === 'target' ? targetDisplayText : sourceDisplayText}
                    </Text>
                    {alwaysSelected ? (
                      <Icon
                        className={cn.icon}
                        icon={LockClosed}
                        testId={postfixTestId(props.testId, 'icon')}
                        tooltip={localize('lockClosed.tooltip.label')}
                      />
                    ) : null}
                    {bCustomItem ? (
                      <Icon
                        className={cn.icon}
                        icon={Choice}
                        tooltip={localize('customItem.choiceIcon.tooltip.label')}
                        testId={postfixTestId(props.testId, 'choiceIcon')}
                      />
                    ) : null}
                  </Flow>
                );
              }}
              selectionMode="desktop"
              onSelect={(rows, selectAll) => {
                const selectAllInDisplayData =
                  selectAll === true
                    ? canBeSelectAllInDisplayData(props.displayStyle, displayData)
                      ? true
                      : 'mixed'
                    : selectAll;
                const rowsInDisplayData =
                  selectAll === true && (!rows || rows.size === 0)
                    ? displayData.map((_displayItem, index) => index.toString())
                    : Array.from(rows ?? []);

                // do not allow folders to be selected
                rowsInDisplayData.filter(
                  (rowIndex) => displayData[parseInt(rowIndex)]?.data.hasChildren === true
                );

                setSelectionState({
                  selectAll: selectAllInDisplayData,
                  selectedRows: rowsInDisplayData ? [...rowsInDisplayData] : undefined,
                });
              }}
              isRowDisabled={(key) =>
                props.tableName !== 'target' && (props.data[parseInt(key)]?._disabled ?? false)
              }
              initialFocusVisible={props.focusAfterAction}
              initialFocus={
                selectionsInDisplayData &&
                selectionsInDisplayData.length > 0 &&
                props.focusAfterAction
                  ? {
                      rowIndex: parseInt(selectionsInDisplayData[0]),
                      region: TableRegions.Data,
                      colIndex: 0,
                    }
                  : focusedCell.current
              }
              onFocusChange={(focus) => {
                focusedCell.current = focus;
              }}
              cellBordersVisible={false}
              columnHeadersVisible={false}
              onDoubleClick={({ rowIndex }) => {
                props.onDoubleClick(displayData[rowIndex].originRowIndex, props.tableName);
              }}
              emptyDataProps={{ text: zeroStateText }}
              hideManageColumnsButton
              rowHeight={(rowIndex) => {
                if (descriptionField && displayData[rowIndex]?.data[descriptionField]) {
                  return 40;
                } else {
                  return 28;
                }
              }}
              removeFillRows
              accessibilityInfo={{
                getCellInfo: (props) => {
                  let message: string | null = null;
                  let prefix = '';
                  const text: string[] = [];
                  const itemData = displayedData[props.rowIndex]?.data;
                  if (itemData) {
                    if (itemData._alwaysSelected) {
                      prefix = localize('lockClosed.aria');
                      text.push(prefix);
                    }

                    if (customItems?.includes(itemData)) {
                      message = localize('customItemSelection.msg', {
                        customItemSelected: itemData[key].toString(),
                      });
                    } else if (descriptionField && itemData[descriptionField]) {
                      message = `${itemData[key].toString()} ${itemData[
                        descriptionField
                      ].toString()}`;
                    } else {
                      message = itemData[key].toString();
                    }
                  }
                  return prefix.length > 0 ? `${prefix} ${message}` : message;
                },
              }}
              // TODO: remove. temp workaround for NOVACOMMONS-12044
              setSelectAllRows={numberOfRows === 0 ? undefined : tableState.setSelectAllRows}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
});

export function generateSingleDisplayData(originData: Array<Item>) {
  const displayData: Array<DisplayItem> = [];
  originData.forEach((item, index) => {
    const displayDataIndex = displayData.findIndex(
      (displayItem) => JSON.stringify(item) === JSON.stringify(displayItem.data)
    );
    if (displayDataIndex <= -1) {
      displayData.push({ data: item, count: 1, originRowIndex: index, key: `${index}` });
    } else {
      displayData[displayDataIndex].count++;
      // if duplicated items found, the originRowIndex will always indicate the last one.
      displayData[displayDataIndex].originRowIndex = index;
    }
  });
  return displayData;
}

function generateMultipleDisplayData(originData: Array<Item>, type: string) {
  const displayData = originData.map((item, index) => {
    // very first time - we index the original items for tree structures
    if (type === 'tree' && item.index === undefined) {
      item.index = index;
    }

    return {
      data: item,
      count: 1,
      originRowIndex: index,
      key: `${index}`,
    } as DisplayItem;
  });

  return displayData
    .filter((item) => {
      const siblings = getAllLevelsChildNodesRowKey(item.key, displayData).filter((key) => {
        // do not include folders
        const item = displayData.find((value) => value.key === key);
        return item && !item.data.hasChildren;
      });
      return !item.data.hasChildren || siblings.length > 0;
    })
    .map((item, index) => {
      return { ...item, key: `${index}` };
    });
}

function getSelectionsInDisplayData(
  selectionsInOriginData: Array<string>,
  originData: Array<Item>,
  displayData: Array<DisplayItem>
) {
  const result = selectionsInOriginData.map((selection) => {
    const originRowIndex = parseInt(selection);
    let displayItemIndex = displayData.findIndex((displayItem) => {
      return displayItem.originRowIndex === originRowIndex;
    });
    if (displayItemIndex === -1) {
      const originItem = originData[originRowIndex];
      displayItemIndex = displayData.findIndex((displayItem) => {
        return displayItem.data === originItem;
      });
    }
    return displayItemIndex.toString();
  });
  return result;
}

function haveDuplicatedItems(displayItems: Array<DisplayItem>) {
  return displayItems.findIndex((displayItem) => displayItem.count > 1) > -1;
}

function canBeSelectAllInDisplayData(
  displayStyle: DisplayStyle = 'multiple',
  displayData: Array<DisplayItem>
) {
  return displayStyle === 'multiple' || !haveDuplicatedItems(displayData);
}
